import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import { RequirePermissions } from "@/modules/auth/presentation/ui/RequirePermissions";
import { LazyPage } from "@/shared/ui/LazyPage";

const RootLayout = lazy(() =>
  import("./layout/RootLayout").then((module) => ({
    default: module.RootLayout,
  })),
);
const ErrorBoundary = lazy(() =>
  import("./error/ErrorBoundary").then((module) => ({
    default: module.ErrorBoundary,
  })),
);
const AuthLayout = lazy(() =>
  import("./layout/AuthLayout").then((module) => ({
    default: module.AuthLayout,
  })),
);

const LoginPage = lazy(() =>
  import("@/modules/auth/presentation/pages/LoginPage").then((module) => ({
    default: module.LoginPage,
  })),
);
const AuthenticatedLayout = lazy(() =>
  import("./layout/AuthenticatedLayout").then((module) => ({
    default: module.AuthenticatedLayout,
  })),
);
const DashboardPage = lazy(() =>
  import("@/modules/dashboard/presentation/pages/DashboardPage").then(
    (module) => ({
      default: module.DashboardPage,
    }),
  ),
);
const ReportsChartsPage = lazy(() =>
  import("@/modules/reports/presentation/pages/ReportsChartsPage").then(
    (module) => ({
      default: module.ReportsChartsPage,
    }),
  ),
);
const RolesPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RolesPage").then((module) => ({
    default: module.RolesPage,
  })),
);
const RoleCreatePage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleCreatePage").then(
    (module) => ({
      default: module.RoleCreatePage,
    }),
  ),
);
const RoleEditPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleEditPage").then((module) => ({
    default: module.RoleEditPage,
  })),
);
const UsersPage = lazy(() =>
  import("@/modules/users/presentation/pages/UsersPage").then((module) => ({
    default: module.UsersPage,
  })),
);
const UserCreatePage = lazy(() =>
  import("@/modules/users/presentation/pages/UserCreatePage").then(
    (module) => ({
      default: module.UserCreatePage,
    }),
  ),
);
const UserEditPage = lazy(() =>
  import("@/modules/users/presentation/pages/UserEditPage").then((module) => ({
    default: module.UserEditPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@/modules/auth/presentation/pages/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  })),
);
const Forbidden = lazy(() =>
  import("./error/Forbidden").then((module) => ({
    default: module.Forbidden,
  })),
);

const ProvincesPage = lazy(() =>
  import("@/modules/geo/presentation/pages/ProvincesPage").then((module) => ({
    default: module.ProvincesPage,
  })),
);
const ProvinceCreatePage = lazy(() =>
  import("@/modules/geo/presentation/pages/ProvinceCreatePage").then(
    (module) => ({
      default: module.ProvinceCreatePage,
    }),
  ),
);

const ProvinceEditPage = lazy(() =>
  import("@/modules/geo/presentation/pages/ProvinceEditPage").then(
    (module) => ({
      default: module.ProvinceEditPage,
    }),
  ),
);

const DistrictsPage = lazy(() =>
  import("../modules/geo/presentation/pages/DistrictsPage").then((module) => ({
    default: module.DistrictsPage,
  })),
);
const DistrictCreatePage = lazy(() =>
  import("../modules/geo/presentation/pages/DistrictCreatePage").then(
    (module) => ({
      default: module.DistrictCreatePage,
    }),
  ),
);
const DistrictEditPage = lazy(() =>
  import("../modules/geo/presentation/pages/DistrictEditPage").then(
    (module) => ({
      default: module.DistrictEditPage,
    }),
  ),
);

const ZonesPage = lazy(() =>
  import("@/modules/shipping/presentation/pages/ZonesPage").then((module) => ({
    default: module.ZonesPage,
  })),
);
const ZoneCreatePage = lazy(() =>
  import("@/modules/shipping/presentation/pages/ZoneCreatePage").then(
    (module) => ({
      default: module.ZoneCreatePage,
    }),
  ),
);
const ZoneEditPage = lazy(() =>
  import("@/modules/shipping/presentation/pages/ZoneEditPage").then(
    (module) => ({
      default: module.ZoneEditPage,
    }),
  ),
);
const RatesPage = lazy(() =>
  import("@/modules/shipping/presentation/pages/RatesPage").then((module) => ({
    default: module.RatesPage,
  })),
);
const RateCreatePage = lazy(() =>
  import("@/modules/shipping/presentation/pages/RateCreatePage").then(
    (module) => ({
      default: module.RateCreatePage,
    }),
  ),
);
const RateEditPage = lazy(() =>
  import("@/modules/shipping/presentation/pages/RateEditPage").then(
    (module) => ({
      default: module.RateEditPage,
    }),
  ),
);
const ShippingSettingsPage = lazy(() =>
  import("@/modules/shipping/presentation/pages/ShippingSettingsPage").then(
    (module) => ({
      default: module.ShippingSettingsPage,
    }),
  ),
);
const CarriersPage = lazy(() =>
  import("@/modules/shipping/presentation/pages/CarriersPage").then((m) => ({
    default: m.CarriersPage,
  })),
);
const CarrierCreatePage = lazy(() =>
  import("@/modules/shipping/presentation/pages/CarrierCreatePage").then(
    (m) => ({ default: m.CarrierCreatePage }),
  ),
);
const CarrierEditPage = lazy(() =>
  import("@/modules/shipping/presentation/pages/CarrierEditPage").then((m) => ({
    default: m.CarrierEditPage,
  })),
);

const CollectionsPage = lazy(() =>
  import("@/modules/collections/presentation/pages/CollectionsPage").then(
    (module) => ({
      default: module.CollectionsPage,
    }),
  ),
);
const CollectionCreatePage = lazy(() =>
  import("@/modules/collections/presentation/pages/CollectionCreatePage").then(
    (module) => ({
      default: module.CollectionCreatePage,
    }),
  ),
);
const CollectionEditPage = lazy(() =>
  import("@/modules/collections/presentation/pages/CollectionEditPage").then(
    (module) => ({
      default: module.CollectionEditPage,
    }),
  ),
);
const OptionsPage = lazy(() =>
  import("@/modules/options/presentation/pages/OptionsPage").then((module) => ({
    default: module.OptionsPage,
  })),
);
const OptionCreatePage = lazy(() =>
  import("@/modules/options/presentation/pages/OptionCreatePage").then(
    (module) => ({
      default: module.OptionCreatePage,
    }),
  ),
);
const OptionEditPage = lazy(() =>
  import("@/modules/options/presentation/pages/OptionEditPage").then(
    (module) => ({
      default: module.OptionEditPage,
    }),
  ),
);

const CategoriesPage = lazy(() =>
  import("@/modules/categories/presentation/pages/CategoriesPage").then(
    (module) => ({
      default: module.CategoriesPage,
    }),
  ),
);
const CategoryCreatePage = lazy(() =>
  import("@/modules/categories/presentation/pages/CategoryCreatePage").then(
    (module) => ({
      default: module.CategoryCreatePage,
    }),
  ),
);
const CategoryEditPage = lazy(() =>
  import("@/modules/categories/presentation/pages/CategoryEditPage").then(
    (module) => ({
      default: module.CategoryEditPage,
    }),
  ),
);

const ProductsPage = lazy(() =>
  import("@/modules/products/presentation/pages/ProductsPage").then(
    (module) => ({
      default: module.ProductsPage,
    }),
  ),
);
const ProductDetailPage = lazy(() =>
  import("@/modules/products/presentation/pages/ProductDetailPage").then(
    (module) => ({
      default: module.ProductDetailPage,
    }),
  ),
);
const ProductCreatePage = lazy(() =>
  import("@/modules/products/presentation/pages/ProductCreatePage").then(
    (module) => ({
      default: module.ProductCreatePage,
    }),
  ),
);
const ProductEditPage = lazy(() =>
  import("@/modules/products/presentation/pages/ProductEditPage").then(
    (module) => ({
      default: module.ProductEditPage,
    }),
  ),
);
const ProductionProductsPage = lazy(() =>
  import("@/modules/products/presentation/pages/ProductionProductsPage").then(
    (module) => ({
      default: module.ProductionProductsPage,
    }),
  ),
);
const ProductionProductDetailPage = lazy(() =>
  import(
    "@/modules/products/presentation/pages/ProductionProductDetailPage"
  ).then((module) => ({
    default: module.ProductionProductDetailPage,
  })),
);
const ProductionProductCreatePage = lazy(() =>
  import(
    "@/modules/products/presentation/pages/ProductionProductCreatePage"
  ).then((module) => ({
    default: module.ProductionProductCreatePage,
  })),
);
const ProductionProductEditPage = lazy(() =>
  import("@/modules/products/presentation/pages/ProductionProductEditPage").then(
    (module) => ({
      default: module.ProductionProductEditPage,
    }),
  ),
);
const OnlineShowcaseProductsPage = lazy(() =>
  import(
    "@/modules/products/presentation/pages/OnlineShowcaseProductsPage"
  ).then((module) => ({
    default: module.OnlineShowcaseProductsPage,
  })),
);
const OnlineShowcaseProductDetailPage = lazy(() =>
  import(
    "@/modules/products/presentation/pages/OnlineShowcaseProductDetailPage"
  ).then((module) => ({
    default: module.OnlineShowcaseProductDetailPage,
  })),
);
const OnlineShowcaseProductCreatePage = lazy(() =>
  import(
    "@/modules/products/presentation/pages/OnlineShowcaseProductCreatePage"
  ).then((module) => ({
    default: module.OnlineShowcaseProductCreatePage,
  })),
);
const OnlineShowcaseProductEditPage = lazy(() =>
  import(
    "@/modules/products/presentation/pages/OnlineShowcaseProductEditPage"
  ).then((module) => ({
    default: module.OnlineShowcaseProductEditPage,
  })),
);

const OrdersPage = lazy(() =>
  import("@/modules/orders/presentation/pages/OrdersPage").then((module) => ({
    default: module.OrdersPage,
  })),
);
const OrderDetailPage = lazy(() =>
  import("@/modules/orders/presentation/pages/OrderDetailPage").then(
    (module) => ({
      default: module.OrderDetailPage,
    }),
  ),
);
const PosPage = lazy(() =>
  import("@/modules/pos/presentation/pages/PosPage").then((module) => ({
    default: module.PosPage,
  })),
);
const PaymentsPage = lazy(() =>
  import("@/modules/payments/presentation/pages/PaymentsPage").then(
    (module) => ({
      default: module.PaymentsPage,
    }),
  ),
);
const PaymentDetailPage = lazy(() =>
  import("@/modules/payments/presentation/pages/PaymentDetailPage").then(
    (module) => ({
      default: module.PaymentDetailPage,
    }),
  ),
);

const CustomersPage = lazy(() =>
  import("@/modules/customers/presentation/pages/CustomersPage").then(
    (module) => ({
      default: module.CustomersPage,
    }),
  ),
);
const CustomerDetailPage = lazy(() =>
  import("@/modules/customers/presentation/pages/CustomerDetailPage").then(
    (module) => ({
      default: module.CustomerDetailPage,
    }),
  ),
);
const MemberTiersPage = lazy(() =>
  import("@/modules/customers/presentation/pages/MemberTiersPage").then(
    (module) => ({
      default: module.MemberTiersPage,
    }),
  ),
);
const CustomerGroupsPage = lazy(() =>
  import("@/modules/customers/presentation/pages/CustomerGroupsPage").then(
    (module) => ({
      default: module.CustomerGroupsPage,
    }),
  ),
);
const ReviewsPage = lazy(() =>
  import("@/modules/reviews/presentation/pages/ReviewsPage").then((module) => ({
    default: module.ReviewsPage,
  })),
);
const CmsSectionsPage = lazy(() =>
  import("@/modules/cms/presentation/pages/CmsSectionsPage").then((module) => ({
    default: module.CmsSectionsPage,
  })),
);
const CmsSectionEditPage = lazy(() =>
  import("@/modules/cms/presentation/pages/CmsSectionEditPage").then(
    (module) => ({
      default: module.CmsSectionEditPage,
    }),
  ),
);
const CmsSectionCreatePage = lazy(() =>
  import("@/modules/cms/presentation/pages/CmsSectionCreatePage").then(
    (module) => ({
      default: module.CmsSectionCreatePage,
    }),
  ),
);
const StockListPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/StockListPage").then(
    (module) => ({
      default: module.StockListPage,
    }),
  ),
);
const StockMovementsPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/StockMovementsPage").then(
    (module) => ({
      default: module.StockMovementsPage,
    }),
  ),
);
const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: ErrorBoundary,
  beforeLoad: ({ location }) => {
    if (
      location.pathname === "/" ||
      location.pathname === "/app" ||
      location.pathname === "/app/"
    ) {
      throw redirect({ to: "/app/dashboard", replace: true });
    }
  },
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: AuthenticatedLayout,
});

const provincesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/geo/provinces",
  component: () => (
    <RequirePermissions all={["geo:read"]}>
      <LazyPage>
        <ProvincesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const provinceCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/geo/provinces/new",
  component: () => (
    <RequirePermissions all={["geo:create"]}>
      <LazyPage>
        <ProvinceCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const provinceEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/geo/provinces/$id/edit",
  component: () => (
    <RequirePermissions all={["geo:update"]}>
      <LazyPage>
        <ProvinceEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const districtsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/geo/districts",
  component: () => (
    <RequirePermissions all={["geo:read"]}>
      <LazyPage>
        <DistrictsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const districtCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/geo/districts/new",
  component: () => (
    <RequirePermissions all={["geo:create"]}>
      <LazyPage>
        <DistrictCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const districtEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/geo/districts/$id/edit",
  component: () => (
    <RequirePermissions all={["geo:update"]}>
      <LazyPage>
        <DistrictEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const shippingZonesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shipping/zones",
  component: () => (
    <RequirePermissions all={["shipping:read"]}>
      <LazyPage>
        <ZonesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const shippingZoneCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shipping/zones/new",
  component: () => (
    <RequirePermissions all={["shipping:create"]}>
      <LazyPage>
        <ZoneCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const shippingZoneEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shipping/zones/$id/edit",
  component: () => (
    <RequirePermissions all={["shipping:update"]}>
      <LazyPage>
        <ZoneEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const shippingRatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shipping/rates",
  component: () => (
    <RequirePermissions all={["shipping:read"]}>
      <LazyPage>
        <RatesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const shippingRateCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shipping/rates/new",
  component: () => (
    <RequirePermissions all={["shipping:create"]}>
      <LazyPage>
        <RateCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const shippingRateEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shipping/rates/$id/edit",
  component: () => (
    <RequirePermissions all={["shipping:update"]}>
      <LazyPage>
        <RateEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const shippingSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shipping/settings",
  component: () => (
    <RequirePermissions all={["shipping:update"]}>
      <LazyPage>
        <ShippingSettingsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const shippingCarriersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shipping/carriers",
  component: () => (
    <RequirePermissions all={["shipping:read"]}>
      <LazyPage>
        <CarriersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const shippingCarrierCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shipping/carriers/new",
  component: () => (
    <RequirePermissions all={["shipping:create"]}>
      <LazyPage>
        <CarrierCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const shippingCarrierEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shipping/carriers/$id/edit",
  component: () => (
    <RequirePermissions all={["shipping:update"]}>
      <LazyPage>
        <CarrierEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const collectionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/collections",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <CollectionsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const collectionCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/collections/new",
  component: () => (
    <RequirePermissions all={["catalog:create"]}>
      <LazyPage>
        <CollectionCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const collectionEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/collections/$id/edit",
  component: () => (
    <RequirePermissions all={["catalog:update"]}>
      <LazyPage>
        <CollectionEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const optionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/options",
  component: () => (
    <RequirePermissions all={["options:read"]}>
      <LazyPage>
        <OptionsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const optionCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/options/new",
  component: () => (
    <RequirePermissions all={["options:create"]}>
      <LazyPage>
        <OptionCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const optionEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/options/$id/edit",
  component: () => (
    <RequirePermissions all={["options:update"]}>
      <LazyPage>
        <OptionEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const reviewsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reviews",
  component: () => (
    <RequirePermissions all={["reviews:read"]}>
      <LazyPage>
        <ReviewsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const cmsSectionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cms/sections",
  component: () => (
    <RequirePermissions all={["cms:read"]}>
      <LazyPage>
        <CmsSectionsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const cmsSectionCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cms/sections/new",
  component: () => (
    <RequirePermissions all={["cms:create"]}>
      <LazyPage>
        <CmsSectionCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const cmsSectionEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cms/sections/$id/edit",
  component: () => (
    <RequirePermissions all={["cms:update"]}>
      <LazyPage>
        <CmsSectionEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const SettingsPage = lazy(() =>
  import("@/modules/settings/presentation/pages/SettingsPage").then(
    (module) => ({ default: module.SettingsPage }),
  ),
);
const SocialSalesSourcesPage = lazy(() =>
  import(
    "@/modules/social-sales-sources/presentation/pages/SocialSalesSourcesPage"
  ).then((module) => ({ default: module.SocialSalesSourcesPage })),
);
const SocialSalesSourceCreatePage = lazy(() =>
  import(
    "@/modules/social-sales-sources/presentation/pages/SocialSalesSourceCreatePage"
  ).then((module) => ({ default: module.SocialSalesSourceCreatePage })),
);
const SocialSalesSourceEditPage = lazy(() =>
  import(
    "@/modules/social-sales-sources/presentation/pages/SocialSalesSourceEditPage"
  ).then((module) => ({ default: module.SocialSalesSourceEditPage })),
);
const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: () => (
    <RequirePermissions all={["settings:read"]} any={["settings:update"]}>
      <LazyPage>
        <SettingsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const socialSalesSourcesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/social-sales-sources",
  component: () => (
    <RequirePermissions all={["social_sources:read"]}>
      <LazyPage>
        <SocialSalesSourcesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const socialSalesSourceCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/social-sales-sources/new",
  component: () => (
    <RequirePermissions all={["social_sources:create"]}>
      <LazyPage>
        <SocialSalesSourceCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const socialSalesSourceEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/social-sales-sources/$id/edit",
  component: () => (
    <RequirePermissions all={["social_sources:update"]}>
      <LazyPage>
        <SocialSalesSourceEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const categoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/categories",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <CategoriesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const categoryCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/categories/new",
  component: () => (
    <RequirePermissions all={["catalog:create"]}>
      <LazyPage>
        <CategoryCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const categoryEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/categories/$id/edit",
  component: () => (
    <RequirePermissions all={["catalog:update"]}>
      <LazyPage>
        <CategoryEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const productsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/products",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <ProductsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const productDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/products/$id",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <ProductDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const productCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/products/new",
  component: () => (
    <RequirePermissions all={["catalog:create"]}>
      <LazyPage>
        <ProductCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const productEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/products/$id/edit",
  component: () => (
    <RequirePermissions all={["catalog:update"]}>
      <LazyPage>
        <ProductEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const productionProductsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/production",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <ProductionProductsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const productionProductDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/production/$id",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <ProductionProductDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const productionProductCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/production/new",
  component: () => (
    <RequirePermissions all={["catalog:create"]}>
      <LazyPage>
        <ProductionProductCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const productionProductEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/production/$id/edit",
  component: () => (
    <RequirePermissions all={["catalog:update"]}>
      <LazyPage>
        <ProductionProductEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const onlineShowcaseProductsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/online-showcase",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <OnlineShowcaseProductsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const onlineShowcaseProductDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/online-showcase/$id",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <OnlineShowcaseProductDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const onlineShowcaseProductCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/online-showcase/new",
  component: () => (
    <RequirePermissions all={["catalog:create"]}>
      <LazyPage>
        <OnlineShowcaseProductCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const onlineShowcaseProductEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/online-showcase/$id/edit",
  component: () => (
    <RequirePermissions all={["catalog:update"]}>
      <LazyPage>
        <OnlineShowcaseProductEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const ordersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <OrdersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const orderDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders/$id",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <OrderDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const posRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/pos",
  component: () => (
    <RequirePermissions all={["pos:checkout", "inventory:read"]}>
      <LazyPage>
        <PosPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const paymentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/payments",
  component: () => (
    <RequirePermissions all={["payments:read"]}>
      <LazyPage>
        <PaymentsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const paymentDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/payments/$id",
  component: () => (
    <RequirePermissions all={["payments:read"]}>
      <LazyPage>
        <PaymentDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers",
  component: () => (
    <RequirePermissions all={["customers:read"]}>
      <LazyPage>
        <CustomersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const customerDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers/$id",
  component: () => (
    <RequirePermissions all={["customers:read"]}>
      <LazyPage>
        <CustomerDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const memberTiersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers/tiers",
  component: () => (
    <RequirePermissions all={["customers:read"]}>
      <LazyPage>
        <MemberTiersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const customerGroupsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers/groups",
  component: () => (
    <RequirePermissions all={["customers:read"]}>
      <LazyPage>
        <CustomerGroupsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const stockListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/stock",
  component: () => (
    <RequirePermissions all={["inventory:read"]}>
      <LazyPage>
        <StockListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const stockMovementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/movements",
  component: () => (
    <RequirePermissions all={["inventory:read"]}>
      <LazyPage>
        <StockMovementsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboard",
  component: () => (
    <LazyPage>
      <DashboardPage />
    </LazyPage>
  ),
});

const reportsChartsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports",
  component: () => (
    <RequirePermissions all={["payments:read"]}>
      <LazyPage>
        <ReportsChartsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RolesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/create",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RoleCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/$id/edit",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RoleEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users",
  component: () => (
    <RequirePermissions all={["users:read"]}>
      <LazyPage>
        <UsersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/create",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:create"]}>
      <LazyPage>
        <UserCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/$id/edit",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:update"]}>
      <LazyPage>
        <UserEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile",
  component: () => (
    <LazyPage>
      <ProfilePage />
    </LazyPage>
  ),
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/errors/forbidden",
  component: () => (
    <LazyPage>
      <Forbidden />
    </LazyPage>
  ),
});

export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([loginRoute]),
  appRoute.addChildren([
    dashboardRoute,
    reportsChartsRoute,
    rolesRoute,
    roleCreateRoute,
    roleEditRoute,
    usersRoute,
    userCreateRoute,
    userEditRoute,
    profileRoute,
    provincesRoute,
    provinceCreateRoute,
    provinceEditRoute,
    districtsRoute,
    districtCreateRoute,
    districtEditRoute,
    shippingZonesRoute,
    shippingZoneCreateRoute,
    shippingZoneEditRoute,
    shippingRatesRoute,
    shippingRateCreateRoute,
    shippingRateEditRoute,
    shippingSettingsRoute,
    shippingCarriersRoute,
    shippingCarrierCreateRoute,
    shippingCarrierEditRoute,
    collectionsRoute,
    collectionCreateRoute,
    collectionEditRoute,
    optionsRoute,
    optionCreateRoute,
    optionEditRoute,
    categoriesRoute,
    categoryCreateRoute,
    categoryEditRoute,
    productsRoute,
    productDetailRoute,
    productCreateRoute,
    productEditRoute,
    productionProductsRoute,
    productionProductDetailRoute,
    productionProductCreateRoute,
    productionProductEditRoute,
    onlineShowcaseProductsRoute,
    onlineShowcaseProductDetailRoute,
    onlineShowcaseProductCreateRoute,
    onlineShowcaseProductEditRoute,
    ordersRoute,
    orderDetailRoute,
    posRoute,
    paymentsRoute,
    paymentDetailRoute,
    customersRoute,
    customerDetailRoute,
    memberTiersRoute,
    customerGroupsRoute,
    reviewsRoute,
    cmsSectionsRoute,
    cmsSectionCreateRoute,
    cmsSectionEditRoute,
    settingsRoute,
    socialSalesSourcesRoute,
    socialSalesSourceCreateRoute,
    socialSalesSourceEditRoute,
    stockListRoute,
    stockMovementsRoute,
  ]),
  forbiddenRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
